import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import bearLogo from '../../assets/bear-logo.png';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', confirm: '', legal_name: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);

    const { data, error: signUpErr } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { legal_name: form.legal_name } },
    });

    if (signUpErr) { setError(signUpErr.message); setLoading(false); return; }

    // If identities array is empty, this email already exists in the system
    // (either invited by admin or previously registered). Show inbox message.
    if (data.user?.identities?.length === 0) {
      setLoading(false);
      setError('An account with this email already exists. If you were invited by BearSecurity, check your inbox for the invite email and use that link to set your password.');
      return;
    }

    // Create/refresh profile row with role=officer, status=pending.
    // Upsert (onConflict: id) — handles cases where a row already exists
    // (admin invite, prior partial signup, auth trigger) without 409-ing.
    if (data.user) {
      await supabase.from('profiles').upsert({
        id: data.user.id,
        email: form.email,
        legal_name: form.legal_name,
        role: 'officer',
        status: 'pending',
        onboarding_complete: false,
      }, { onConflict: 'id' });

      // Auto-link any pending referral that targeted this email so the
      // Referred tab clears once the person registers. Fire-and-forget:
      // a failure here must not block the user's signup.
      try {
        const { data: matchingReferral } = await supabase
          .from('referrals')
          .select('id')
          .ilike('referred_email', form.email.trim())
          .eq('status', 'pending')
          .maybeSingle();
        if (matchingReferral) {
          await supabase.from('referrals').update({
            status: 'onboarded',
            referred_guard_id: data.user.id,
          }).eq('id', matchingReferral.id);
        }
      } catch (referralErr) {
        console.error('Referral auto-link failed:', referralErr?.message || referralErr);
      }
    }

    setLoading(false);
    setSuccess(true);
  }

  if (success) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
        <img src={bearLogo} alt="BearSecurity" style={{ width: 48, height: 48, objectFit: 'contain' }} />
        <h2 style={{ marginTop: '1rem' }}>Account Created</h2>
        <p style={{ color: 'var(--color-muted)', marginTop: '0.5rem' }}>
          Check your email to confirm your address, then sign in to complete onboarding.
        </p>
        <Button onClick={() => navigate('/login')} style={{ marginTop: '1.5rem' }}>
          Go to Sign In
        </Button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--color-header)' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.5rem 1rem', color: '#fff', gap: '0.5rem' }}>
        <img src={bearLogo} alt="BearSecurity" style={{ width: 48, height: 48, objectFit: 'contain' }} />
        <h1 style={{ fontSize: '1.25rem' }}>BearSecurity</h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>Officer Registration</p>
      </div>

      <div style={{ background: 'var(--color-bg)', borderRadius: '20px 20px 0 0', padding: '1.75rem 1.5rem 2rem' }}>
        <h2 style={{ marginBottom: '1.25rem' }}>Create Account</h2>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Legal Name" value={form.legal_name} onChange={set('legal_name')} placeholder="First Last" required />
          <Input label="Email" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" required />
          <Input label="Password" type="password" value={form.password} onChange={set('password')} placeholder="Min 8 characters" required />
          <Input label="Confirm Password" type="password" value={form.confirm} onChange={set('confirm')} placeholder="Repeat password" required />

          {error && <p style={{ color: 'var(--color-danger)', fontSize: '0.875rem' }}>{error}</p>}

          <Button type="submit" fullWidth disabled={loading}>
            {loading ? 'Creating account…' : 'Create Account'}
          </Button>
        </form>

        <p style={{ marginTop: '1.25rem', textAlign: 'center', fontSize: '0.875rem', color: 'var(--color-muted)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--color-accent)', fontWeight: 600 }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
